import { commonRunEditState } from "./common_run_edit_state";
import { calculationFirstCellPrompt } from "./cell_explanation_prompt";
import {
  canonicalLocaleDecimalMax2,
  formatLocaleDecimalMax2,
} from "./locale_decimal_max_2";

export type InventoryHealthVariant =
  | "standard"
  | "with_transfers"
  | "for_hubs"
  | "raw";

export type InventoryHealthPeriodField = "day" | "week" | "month";

export type CommonInventoryHealthCellContextV1 = Readonly<{
  version: "CommonInventoryHealthCellContext.v1";
  surfaceId: "inventory-health";
  variant: InventoryHealthVariant;
  measure: "ending_inventory";
  cellKind: "period_leaf";
  period: Readonly<{
    field: InventoryHealthPeriodField;
    grain: InventoryHealthPeriodField;
    startEpochMs: number;
    endExclusiveEpochMs: number;
  }>;
  dimensions: readonly Readonly<{ field: string; value: string }>[];
  filters: readonly [];
  postFilters: readonly [];
  display: Readonly<{
    value: string;
    unit: "units";
    rule: "locale_decimal_max_2";
  }>;
  state: Readonly<{
    basis: "base";
    edits: "none" | "saved";
    comparison: false;
  }>;
}>;

export type CommonInventoryHealthCellContextV2 = Readonly<{
  version: "CommonInventoryHealthCellContext.v2";
  surfaceId: "inventory-health";
  variant: InventoryHealthVariant;
  receiptId: string;
  measure: "ending_inventory";
  cellKind: "period_leaf";
  period: Readonly<{
    field: InventoryHealthPeriodField;
    grain: InventoryHealthPeriodField;
    startEpochMs: number;
    endExclusiveEpochMs: number;
  }>;
  dimensions: readonly Readonly<{ field: string; value: string }>[];
  selector: Readonly<{
    segmentation: string;
    measure: "ending inventory on hand";
    measureRank: number;
    columnKey: string;
  }>;
  filters: readonly [];
  postFilters: readonly [];
  display: Readonly<{
    rawValue: string;
    unit: "units";
    rule: "inventory_matrix_v1";
  }>;
  state: Readonly<{
    basis: "base";
    edits: "none" | "saved";
    comparison: false;
  }>;
}>;

export type InventoryHealthCellContextInput = Readonly<{
  variant: unknown;
  measure: unknown;
  periodField: unknown;
  periodStartEpochMs: unknown;
  segments: readonly unknown[] | undefined;
  row: Readonly<Record<string, unknown>>;
  filters: readonly unknown[] | undefined;
  postFilters: readonly unknown[] | undefined;
  rawValue: unknown;
  settingsNameAndVersion: unknown;
  comparison: unknown;
}>;

export type ReceiptBoundInventoryHealthCellContextInput =
  InventoryHealthCellContextInput &
    Readonly<{
      receiptId: unknown;
      columnKey: unknown;
      selectedMeasure: unknown;
    }>;

const MAX_DIMENSIONS = 32;
const DECIMAL_MAX_2_PATTERN = /^-?(?:0|[1-9][0-9]{0,37})(?:\.[0-9]{1,2})?$/;
const RECEIPT_ID = /^rvr1_[a-f0-9]{64}$/;
const POSITIVE_INTEGER = /^[1-9][0-9]{0,15}$/;

const utf8ByteLength = (value: string): number => {
  let bytes = 0;
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit < 0x80) bytes += 1;
    else if (codeUnit < 0x800) bytes += 2;
    else if (
      codeUnit >= 0xd800 &&
      codeUnit <= 0xdbff &&
      index + 1 < value.length &&
      value.charCodeAt(index + 1) >= 0xdc00 &&
      value.charCodeAt(index + 1) <= 0xdfff
    ) {
      bytes += 4;
      index += 1;
    } else bytes += 3;
  }
  return bytes;
};

const boundedText = (value: unknown, maxBytes: number): string | undefined =>
  typeof value === "string" &&
  value.length > 0 &&
  value === value.trim() &&
  utf8ByteLength(value) <= maxBytes &&
  !/\p{Cc}/u.test(value)
    ? value
    : undefined;

const isInventoryHealthVariant = (
  value: unknown,
): value is InventoryHealthVariant =>
  value === "standard" ||
  value === "with_transfers" ||
  value === "for_hubs" ||
  value === "raw";

const isInventoryHealthPeriodField = (
  value: unknown,
): value is InventoryHealthPeriodField =>
  value === "day" || value === "week" || value === "month";

const periodEndExclusive = (
  field: InventoryHealthPeriodField,
  startEpochMs: unknown,
): number | undefined => {
  if (!Number.isSafeInteger(startEpochMs) || (startEpochMs as number) <= 0) {
    return undefined;
  }
  const start = new Date(startEpochMs as number);
  if (
    Number.isNaN(start.getTime()) ||
    start.getUTCHours() !== 0 ||
    start.getUTCMinutes() !== 0 ||
    start.getUTCSeconds() !== 0 ||
    start.getUTCMilliseconds() !== 0 ||
    (field === "week" && start.getUTCDay() !== 1) ||
    (field === "month" && start.getUTCDate() !== 1)
  ) {
    return undefined;
  }

  const end =
    field === "day"
      ? Date.UTC(
          start.getUTCFullYear(),
          start.getUTCMonth(),
          start.getUTCDate() + 1,
        )
      : field === "week"
        ? Date.UTC(
            start.getUTCFullYear(),
            start.getUTCMonth(),
            start.getUTCDate() + 7,
          )
        : Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1);
  return Number.isSafeInteger(end) && end > (startEpochMs as number)
    ? end
    : undefined;
};

const visibleDecimal = (value: unknown): string | undefined => {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  const canonical = canonicalLocaleDecimalMax2(value);
  if (canonical === undefined) return undefined;
  return DECIMAL_MAX_2_PATTERN.test(canonical) ? canonical : undefined;
};

const canonicalRawValue = (value: unknown): string | undefined => {
  if (
    (typeof value !== "number" && typeof value !== "string") ||
    (typeof value === "string" &&
      (value.length === 0 || value !== value.trim() || value.length > 128))
  ) {
    return undefined;
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return undefined;
  return Object.is(numeric, -0) ? "0" : String(numeric);
};

const canonicalMeasureRank = (value: unknown): number | undefined =>
  Number.isSafeInteger(value) &&
  (value as number) >= 1 &&
  (value as number) <= 5_000_000
    ? (value as number)
    : undefined;

const exactRecipe = (
  variant: InventoryHealthVariant,
): Readonly<{
  periodField: InventoryHealthPeriodField;
  dimensionFields: readonly string[];
}> =>
  variant === "raw"
    ? Object.freeze({
        periodField: "month" as const,
        dimensionFields: Object.freeze(["location"]),
      })
    : Object.freeze({
        periodField: "week" as const,
        dimensionFields: Object.freeze(["sku", "location"]),
      });

const hasExactFields = (
  actual: readonly unknown[] | undefined,
  expected: readonly string[],
): actual is readonly string[] =>
  Array.isArray(actual) &&
  actual.length === expected.length &&
  actual.every((field, index) => field === expected[index]);

const hasExactDimensions = (
  actual: readonly Readonly<{ field: string; value: string }>[],
  expectedFields: readonly string[],
): boolean =>
  actual.length === expectedFields.length &&
  actual.every(
    (dimension, index) =>
      dimension.field === expectedFields[index] &&
      Boolean(boundedText(dimension.value, 512)),
  );

const dimensionsFromInput = (
  segments: readonly unknown[] | undefined,
  row: Readonly<Record<string, unknown>>,
): readonly Readonly<{ field: string; value: string }>[] | undefined => {
  if (
    !Array.isArray(segments) ||
    segments.length === 0 ||
    segments.length > MAX_DIMENSIONS
  ) {
    return undefined;
  }
  const dimensions: Array<Readonly<{ field: string; value: string }>> = [];
  const fields = new Set<string>();
  for (const rawField of segments) {
    const field = boundedText(rawField, 128);
    if (!field || fields.has(field)) return undefined;
    const value = boundedText(row[field], 512);
    if (!value) return undefined;
    fields.add(field);
    dimensions.push(Object.freeze({ field, value }));
  }
  return Object.freeze(dimensions);
};

const hasValidDimensions = (
  dimensions: readonly Readonly<{ field: string; value: string }>[],
): boolean => {
  if (
    !Array.isArray(dimensions) ||
    dimensions.length === 0 ||
    dimensions.length > MAX_DIMENSIONS
  ) {
    return false;
  }
  const fields = new Set<string>();
  const valid = dimensions.every((dimension) => {
    const field = boundedText(dimension?.field, 128);
    const value = boundedText(dimension?.value, 512);
    if (!field || !value || fields.has(field)) return false;
    fields.add(field);
    return true;
  });
  return (
    valid &&
    fields.has("sku") &&
    (fields.size === 1 || (fields.size === 2 && fields.has("location")))
  );
};

export function createInventoryHealthCellContext(
  input: InventoryHealthCellContextInput,
): CommonInventoryHealthCellContextV1 | undefined {
  if (
    !isInventoryHealthVariant(input.variant) ||
    typeof input.measure !== "string" ||
    input.measure.toLowerCase() !== "ending inventory on hand" ||
    !isInventoryHealthPeriodField(input.periodField) ||
    !input.row ||
    typeof input.row !== "object" ||
    Array.isArray(input.row) ||
    input.row.type !== "group" ||
    !Array.isArray(input.filters) ||
    input.filters.length !== 0 ||
    !Array.isArray(input.postFilters) ||
    input.postFilters.length !== 0 ||
    input.comparison !== false
  ) {
    return undefined;
  }

  const endExclusiveEpochMs = periodEndExclusive(
    input.periodField,
    input.periodStartEpochMs,
  );
  const dimensions = dimensionsFromInput(input.segments, input.row);
  const displayValue = visibleDecimal(input.rawValue);
  const edits = commonRunEditState(input.settingsNameAndVersion);
  if (
    !endExclusiveEpochMs ||
    !dimensions ||
    !hasValidDimensions(dimensions) ||
    !displayValue ||
    !edits
  ) {
    return undefined;
  }

  return Object.freeze({
    version: "CommonInventoryHealthCellContext.v1",
    surfaceId: "inventory-health",
    variant: input.variant,
    measure: "ending_inventory",
    cellKind: "period_leaf",
    period: Object.freeze({
      field: input.periodField,
      grain: input.periodField,
      startEpochMs: input.periodStartEpochMs as number,
      endExclusiveEpochMs,
    }),
    dimensions,
    filters: Object.freeze([]) as readonly [],
    postFilters: Object.freeze([]) as readonly [],
    display: Object.freeze({
      value: displayValue,
      unit: "units",
      rule: "locale_decimal_max_2",
    }),
    state: Object.freeze({ basis: "base", edits, comparison: false }),
  });
}

export function createReceiptBoundInventoryHealthCellContext(
  input: ReceiptBoundInventoryHealthCellContextInput,
): CommonInventoryHealthCellContextV2 | undefined {
  if (
    !isInventoryHealthVariant(input.variant) ||
    typeof input.receiptId !== "string" ||
    !RECEIPT_ID.test(input.receiptId) ||
    typeof input.measure !== "string" ||
    input.measure.toLowerCase() !== "ending inventory on hand" ||
    typeof input.selectedMeasure !== "string" ||
    input.selectedMeasure.toLowerCase() !== "ending inventory on hand" ||
    !input.row ||
    typeof input.row !== "object" ||
    Array.isArray(input.row) ||
    input.row.type !== "group" ||
    !Array.isArray(input.filters) ||
    input.filters.length !== 0 ||
    !Array.isArray(input.postFilters) ||
    input.postFilters.length !== 0 ||
    input.comparison !== false
  ) {
    return undefined;
  }

  const recipe = exactRecipe(input.variant);
  if (
    input.periodField !== recipe.periodField ||
    !hasExactFields(input.segments, recipe.dimensionFields) ||
    typeof input.columnKey !== "string" ||
    !POSITIVE_INTEGER.test(input.columnKey) ||
    !Number.isSafeInteger(Number(input.columnKey)) ||
    String(Number(input.columnKey)) !== input.columnKey ||
    Number(input.columnKey) !== input.periodStartEpochMs
  ) {
    return undefined;
  }

  const endExclusiveEpochMs = periodEndExclusive(
    recipe.periodField,
    input.periodStartEpochMs,
  );
  const dimensions = dimensionsFromInput(input.segments, input.row);
  const rawValue = canonicalRawValue(input.rawValue);
  const edits = commonRunEditState(input.settingsNameAndVersion);
  const segmentation = boundedText(input.row.segmentation, 2_048);
  const renderedMeasure = boundedText(
    input.row.measure ?? input.selectedMeasure,
    128,
  );
  const measureRank = canonicalMeasureRank(input.row.measureRank);
  const expectedSegmentation = dimensions
    ?.map(({ value }) => value)
    .join(" | ");
  if (
    !endExclusiveEpochMs ||
    !dimensions ||
    !hasExactDimensions(dimensions, recipe.dimensionFields) ||
    rawValue === undefined ||
    !edits ||
    !segmentation ||
    segmentation !== expectedSegmentation ||
    renderedMeasure !== "ending inventory on hand" ||
    measureRank === undefined
  ) {
    return undefined;
  }

  return Object.freeze({
    version: "CommonInventoryHealthCellContext.v2",
    surfaceId: "inventory-health",
    variant: input.variant,
    receiptId: input.receiptId,
    measure: "ending_inventory",
    cellKind: "period_leaf",
    period: Object.freeze({
      field: recipe.periodField,
      grain: recipe.periodField,
      startEpochMs: input.periodStartEpochMs as number,
      endExclusiveEpochMs,
    }),
    dimensions,
    selector: Object.freeze({
      segmentation,
      measure: renderedMeasure,
      measureRank,
      columnKey: input.columnKey,
    }),
    filters: Object.freeze([]) as readonly [],
    postFilters: Object.freeze([]) as readonly [],
    display: Object.freeze({
      rawValue,
      unit: "units",
      rule: "inventory_matrix_v1",
    }),
    state: Object.freeze({ basis: "base", edits, comparison: false }),
  });
}

export function isSupportedInventoryHealthCellRecipe(
  context: CommonInventoryHealthCellContextV1,
): boolean {
  return (
    context.version === "CommonInventoryHealthCellContext.v1" &&
    context.surfaceId === "inventory-health" &&
    isInventoryHealthVariant(context.variant) &&
    context.measure === "ending_inventory" &&
    context.cellKind === "period_leaf" &&
    isInventoryHealthPeriodField(context.period.field) &&
    context.period.grain === context.period.field &&
    periodEndExclusive(context.period.field, context.period.startEpochMs) ===
      context.period.endExclusiveEpochMs &&
    hasValidDimensions(context.dimensions) &&
    context.filters.length === 0 &&
    context.postFilters.length === 0 &&
    DECIMAL_MAX_2_PATTERN.test(context.display.value) &&
    context.display.value !== "-0" &&
    context.display.unit === "units" &&
    context.display.rule === "locale_decimal_max_2" &&
    context.state.basis === "base" &&
    (context.state.edits === "none" || context.state.edits === "saved") &&
    context.state.comparison === false
  );
}

export function isSupportedReceiptBoundInventoryHealthCellRecipe(
  context: CommonInventoryHealthCellContextV2,
): boolean {
  if (
    context.version !== "CommonInventoryHealthCellContext.v2" ||
    context.surfaceId !== "inventory-health" ||
    !isInventoryHealthVariant(context.variant) ||
    !RECEIPT_ID.test(context.receiptId) ||
    context.measure !== "ending_inventory" ||
    context.cellKind !== "period_leaf"
  ) {
    return false;
  }
  const recipe = exactRecipe(context.variant);
  return (
    context.period.field === recipe.periodField &&
    context.period.grain === recipe.periodField &&
    periodEndExclusive(recipe.periodField, context.period.startEpochMs) ===
      context.period.endExclusiveEpochMs &&
    hasExactDimensions(context.dimensions, recipe.dimensionFields) &&
    context.selector.columnKey === String(context.period.startEpochMs) &&
    context.selector.segmentation ===
      context.dimensions.map(({ value }) => value).join(" | ") &&
    Boolean(boundedText(context.selector.segmentation, 2_048)) &&
    context.selector.measure === "ending inventory on hand" &&
    canonicalMeasureRank(context.selector.measureRank) ===
      context.selector.measureRank &&
    context.filters.length === 0 &&
    context.postFilters.length === 0 &&
    canonicalRawValue(context.display.rawValue) === context.display.rawValue &&
    context.display.unit === "units" &&
    context.display.rule === "inventory_matrix_v1" &&
    context.state.basis === "base" &&
    (context.state.edits === "none" || context.state.edits === "saved") &&
    context.state.comparison === false
  );
}

const periodDescription = (
  period: CommonInventoryHealthCellContextV1["period"],
): string => {
  const date = new Date(period.startEpochMs);
  if (period.grain === "month") {
    return `for ${new Intl.DateTimeFormat("en-US", {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }).format(date)}`;
  }
  const formatted = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
  return period.grain === "week"
    ? `for the week beginning ${formatted}`
    : `on ${formatted}`;
};

export function inventoryHealthCellPrompt(
  context: CommonInventoryHealthCellContextV1,
): string {
  const dimensions = context.dimensions
    .map(({ field, value }) => {
      const friendlyField = field.replace(/_/g, " ");
      const friendlyValue = value === "__NULL__" ? "not specified" : value;
      return `${friendlyField} ${friendlyValue}`;
    })
    .join(" and ");
  const displayedValue = Number(context.display.value).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return calculationFirstCellPrompt(
    `ending inventory of ${displayedValue} units for ${dimensions} ${periodDescription(context.period)}`,
  );
}

export function inventoryHealthReceiptCellPrompt(
  context: CommonInventoryHealthCellContextV2,
): string {
  const dimensions = context.dimensions
    .map(({ field, value }) => {
      const friendlyField =
        field === "sku"
          ? "SKU"
          : field === "location"
            ? "Location"
            : field
                .replace(/_/g, " ")
                .replace(/\b\w/g, (letter) => letter.toUpperCase());
      const friendlyValue = value === "__NULL__" ? "not specified" : value;
      return `${friendlyField}: ${friendlyValue}`;
    })
    .join(" | ");
  const numericValue = Number(context.display.rawValue);
  const displayedValue =
    formatLocaleDecimalMax2(numericValue, "en-US") ?? context.display.rawValue;
  const date = new Date(context.period.startEpochMs);
  const formattedPeriod =
    context.period.grain === "month"
      ? new Intl.DateTimeFormat("en-US", {
          month: "long",
          year: "numeric",
          timeZone: "UTC",
        }).format(date)
      : new Intl.DateTimeFormat("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC",
        }).format(date);
  const periodLabel =
    context.period.grain === "month"
      ? `Month: ${formattedPeriod}`
      : context.period.grain === "week"
        ? `Week of ${formattedPeriod}`
        : `Date: ${formattedPeriod}`;
  return `Explain this ending inventory value: ${displayedValue} units | ${dimensions} | ${periodLabel}.`;
}
